import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Checkbox,
    Button,
    Divider
} from "@mui/material";
import React, { useEffect } from "react";
import { useState } from "react";

export interface ToolOption {
    id: string;
    toolName: string;
    uom: string;
    quantity: number;
    unitPrice: string | number;
}

interface Props {
    open: boolean;
    tools: ToolOption[];
    selectedToolIds: string[];
    onClose: () => void;
    onSubmit: (tools: ToolOption[]) => void;
}

const SelectToolPopup: React.FC<Props> = ({
    open,
    tools,
    selectedToolIds,
    onClose,
    onSubmit
}) => {
    const [localSelected, setLocalSelected] = useState<string[]>([]);

    // When popup opens, reset selection
    useEffect(() => {
        if (open) setLocalSelected([]);
    }, [open]);

    const selectableTools = tools.filter(
        (t) => !selectedToolIds.includes(t.id)
    );

    const isAllSelected =
        selectableTools.length > 0 &&
        localSelected.length === selectableTools.length;
    const isIndeterminate =
        localSelected.length > 0 &&
        localSelected.length < selectableTools.length;

    const handleSelectAll = () => {
        if (isAllSelected) {
            setLocalSelected([]);
        } else {
            setLocalSelected(selectableTools.map((t) => t.id));
        }
    };

    const toggle = (id: string) => {
        if (selectedToolIds.includes(id)) return; // disable if already selected

        setLocalSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleSubmit = () => {
        const selected = tools.filter((t) => localSelected.includes(t.id));
        onSubmit(selected);
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Select Tools</DialogTitle>

            <DialogContent sx={{ maxHeight: 400 }}>
                <List>
                    <ListItem disablePadding>
                        <ListItemButton
                            onClick={handleSelectAll}
                            disabled={selectableTools.length === 0}
                        >
                            <Checkbox
                                checked={isAllSelected}
                                indeterminate={isIndeterminate}
                                disabled={selectableTools.length === 0}
                            />
                            <ListItemText primary="Select All" />
                        </ListItemButton>
                    </ListItem>
                    <Divider />

                    {tools.map((tool) => {
                        const isDisabled = selectedToolIds.includes(tool.id);
                        const isChecked = localSelected.includes(tool.id);

                        return (
                            <ListItem key={tool.id}>
                                <ListItemButton
                                    disabled={isDisabled}
                                    onClick={() => toggle(tool.id)}
                                    sx={{
                                        opacity: isDisabled ? 0.5 : 1
                                    }}
                                >
                                    <Checkbox
                                        checked={isChecked}
                                        disabled={isDisabled}
                                    />
                                    <ListItemText primary={tool.toolName} />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="error">
                    Cancel
                </Button>
                <Button
                    color="primary"
                    disabled={!localSelected.length}
                    onClick={handleSubmit}
                >
                    Add
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SelectToolPopup;
